/**
 * Decide whether a CLOSED tournament table (its gameID appears in the
 * tournament's gotTnmStatus `closedGameIDs`) should be SWEPT — removed from
 * state.tables / playerGames by the App-level closed-table sweep — or KEPT so its
 * in-cell "table closed" prompt (Re-Entry / Observe / Exit) can render and own
 * its own teardown.
 *
 * A closed table is swept ONLY when it is a leftover the player can no longer
 * act on in place — i.e. it is none of:
 *   - the focused single-table (the player is looking right at it),
 *   - the active balancing DESTINATION (protected; transiently listed closed),
 *   - a table the player is SEATED at (gets the seated closed prompt),
 *   - the player's OWN participation table (registered/busted record for it),
 *   - an OPEN tournament cell (MTT/SnG) the player is OBSERVING — this one is
 *     kept so a non-registered railbird still gets the [Observe / Exit] closed
 *     prompt in that cell instead of the cell vanishing under them,
 *   - a table in the middle of FINAL-TABLE BALANCING (isPostBalancing=1).
 *     During a final-table reshuffle the engine transiently lists tables in
 *     closedGameIDs while it moves players onto the final table; sweeping a
 *     cell mid-reshuffle disappears it from the grid before the balancing-
 *     complete gotTexas (isPostBalancing:0) can land — the reported "table
 *     cell closed during final balancing in multi-table grid view" bug.
 *
 * Everything that is NONE of the above is a genuine ghost (e.g. a closed gameID
 * lingering in playerGames with no open cell) and is swept.
 */
export function shouldSweepClosedTable(flags: {
  isClosed: boolean;
  isFocused: boolean;
  isProtected: boolean;
  isSeated: boolean;
  isOwnParticipation: boolean;
  isOpenTournamentCell: boolean;
  isPostBalancing?: boolean;
}): boolean {
  return (
    flags.isClosed &&
    !flags.isFocused &&
    !flags.isProtected &&
    !flags.isSeated &&
    !flags.isOwnParticipation &&
    !flags.isOpenTournamentCell &&
    !flags.isPostBalancing
  );
}

/**
 * A graceful cash-table shutdown ends with one final gotTexas/gotOmaha frame
 * carrying `game.closed = 1`. Closed tournament tables have their own prompt
 * and reconciliation flow, so only cash tables are terminal here.
 */
export function getClosedCashTableUpdate(payload: any):
  | { gameID: string; gameType: string }
  | null {
  const game = payload?.game;
  if (!game || Number(game.closed) !== 1) return null;
  if (game.tabletype === 'm' || game.tabletype === 't') return null;

  const gameID = String(game.gameID ?? '');
  if (!gameID || gameID === '0') return null;

  return {
    gameID,
    gameType: game.game || payload?.gameType || 'texas'
  };
}

/**
 * A pending graceful shutdown removes the server-side waitlist immediately.
 * Return the table identity plus whether this client is already seated: seated
 * players keep their subscription to finish the hand, while observers (which
 * includes waitlisted players) must be removed from the stale local preview.
 */
export function getPendingCashTableUpdate(payload: any, existingTable?: any):
  | { gameID: string; gameType: string; isSeated: boolean }
  | null {
  const game = payload?.game;
  if (!game || Number(game.closing_after_hand) !== 1 || Number(game.closed) === 1) return null;
  if (game.tabletype === 'm' || game.tabletype === 't') return null;

  const gameID = String(game.gameID ?? '');
  if (!gameID || gameID === '0') return null;

  return {
    gameID,
    gameType: game.game || payload?.gameType || 'texas',
    // Table broadcasts carry game state, not the client-derived currentPlayer
    // reliably. Preserve access from the already-loaded table when available.
    isSeated:
      payload?.currentPlayer?.isSeated === true ||
      Number(payload?.currentPlayer?.seatId ?? 0) > 0 ||
      existingTable?.currentPlayer?.isSeated === true ||
      Number(existingTable?.currentPlayer?.seatId ?? 0) > 0
  };
}
